import React, { useState, useEffect } from 'react';

interface SimpleCategory {
  id: string;
  name: string;
}

const AddCategoryPage: React.FC = () => {
  const [categoryName, setCategoryName] = useState('');
  const [parentCategory, setParentCategory] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [existingCategories, setExistingCategories] = useState<SimpleCategory[]>([]);

  // Fetch existing categories
  const fetchExistingCategories = async () => {
    try {
      const response = await fetch('http://localhost:2026/api/categories');
      if (!response.ok) throw new Error('Failed');
      const data: any[] = await response.json();
      const flatCategories: SimpleCategory[] = [];
      const addCategoryAndSubcategories = (category: any, level: number = 0) => {
        const prefix = level === 0 ? '' : '\u00A0\u00A0'.repeat(level * 2) + '└─ ';
        flatCategories.push({ id: category.id.toString(), name: prefix + category.name });
        if (category.subCategories) category.subCategories.forEach((subCat: any) => addCategoryAndSubcategories(subCat, level + 1));
      };
      data.forEach((cat) => addCategoryAndSubcategories(cat, 0));
      setExistingCategories(flatCategories);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchExistingCategories();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('name', categoryName);
      if (parentCategory) formData.append('parentId', parentCategory);
      if (selectedImage) formData.append('categoryImage', selectedImage);

      const response = await fetch('http://localhost:2026/api/categories', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to add category.');

      setMessage({ type: 'success', text: `Category added successfully!` });
      setCategoryName('');
      setParentCategory('');
      setSelectedImage(null);
      fetchExistingCategories();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-10">
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Add New Category</h1>

        {/* Card */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 space-y-6"
        >
          {message && (
            <div
              className={`p-3 mb-4 rounded-md text-sm ${
                message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}
            >
              {message.text}
            </div>
          )}

          {/* Category Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Category Name *</label>
            <input
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              required
              className="mt-1 block w-full p-2.5 text-sm rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-300"
            />
          </div>

          {/* Parent Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Parent Category</label>
            <select
              value={parentCategory}
              onChange={(e) => setParentCategory(e.target.value)}
              className="mt-1 block w-full p-2.5 text-sm rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-300"
            >
              <option value="">-- No Parent Category --</option>
              {existingCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category Icon/Image</label>
            <input
              type="file"
              accept="image/*"
              id="category-image"
              className="hidden"
              onChange={(e) => setSelectedImage(e.target.files ? e.target.files[0] : null)}
            />
            <label
              htmlFor="category-image"
              className="inline-block cursor-pointer px-4 py-2 rounded-xl bg-teal-700 text-white font-medium text-sm hover:bg-teal-600 transition shadow-md"
            >
              {selectedImage ? 'Change Image' : 'Choose Image'}
            </label>
            {selectedImage && <p className="mt-2 text-xs text-gray-600 truncate">{selectedImage.name}</p>}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 rounded-2xl text-sm font-bold text-white tracking-wide transition-all ${
              isSubmitting ? 'bg-gray-400' : 'bg-teal-700 hover:scale-[1.02] hover:shadow-lg'
            }`}
          >
            {isSubmitting ? 'Adding...' : 'Add Category'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddCategoryPage;
